import { DatapointStatus, logUsage, PerformanceDatapoint } from 'owa-analytics';
import { getFileProviderAuthHeaderValue } from 'owa-fileprovider-services';
import { isSuccessStatusCode } from 'owa-http-status-codes';
import AttachmentDataProviderType from 'owa-service/lib/contract/AttachmentDataProviderType';
import fetchDataProviderResourceRequest from 'owa-service/lib/factory/fetchDataProviderResourceRequest';
import fetchDataProviderResourceOperation from 'owa-service/lib/operation/fetchDataProviderResourceOperation';
import { getDelegationTokenForOwa } from 'owa-tokenprovider';
import { trace } from 'owa-trace';
import { POST_METHOD_NAME } from '../utils/constants';

export interface FetchRequestOptions {
    method: string;
    mode: RequestMode;
    body: string;
    datapointNamePrefix: string;
}

export async function fetchService(
    dataProviderType:
        | AttachmentDataProviderType.OneDrivePro
        | AttachmentDataProviderType.OneDriveConsumer,
    requestUrl: string,
    linkUrl: string,
    options: FetchRequestOptions
): Promise<string> {
    let token: string;
    try {
        if (dataProviderType !== AttachmentDataProviderType.OneDriveConsumer) {
            token = await getFileProviderAuthHeaderValue(dataProviderType, false, linkUrl);
        } else {
            token = 'Bearer ' + (await getDelegationTokenForOwa('onedrive_implicit.access'));
        }
    } catch (error) {}

    if (token) {
        logUsage('fetchServiceViaDataProvider');
        trace.info('fetchServiceViaDataProvider');
        return await fetchServiceViaDataProvider(requestUrl, token, options);
    }

    logUsage('fetchServiceViaOWAServer');
    trace.info('fetchServiceViaOWAServer');
    return await fetchServiceViaOWAServer(dataProviderType, requestUrl, options);
}

export async function fetchServiceViaDataProvider(
    requestUrl: string,
    token: string,
    options: FetchRequestOptions
): Promise<string | null> {
    const datapointName = options.datapointNamePrefix + 'DataProvider';
    const datapoint: PerformanceDatapoint = new PerformanceDatapoint(datapointName);
    return fetch(requestUrl, {
        method: options.method,
        mode: options.mode,
        headers: {
            Authorization: token,
            'Content-Type': 'application/json',
            Accept: 'application/json',
        },
        body: options.body,
    })
        .then(response => {
            if (isSuccessStatusCode(response.status)) {
                datapoint.end();
                return response.json();
            } else {
                const error = new Error(
                    `${datapointName} : Response status = ${response.status}, body = ${
                        response.body
                    }`
                );
                datapoint.endWithError(DatapointStatus.ServerError, error);
                throw error;
            }
        })
        .then(data => {
            return JSON.stringify(data);
        });
}

export async function fetchServiceViaOWAServer(
    dataProviderType: AttachmentDataProviderType,
    requestUrl: string,
    options: FetchRequestOptions
): Promise<string | null> {
    const datapointName = options.datapointNamePrefix + 'OWA';
    const datapoint: PerformanceDatapoint = new PerformanceDatapoint(datapointName);

    const fetchRequest = fetchDataProviderResourceRequest({
        ProviderType: dataProviderType,
        Url: requestUrl,
        Method: options.method,
        Body: options.body,
        IsJsonPost: options.body && options.method === POST_METHOD_NAME ? true : false,
    });

    const response = await fetchDataProviderResourceOperation(fetchRequest);
    if (isSuccessStatusCode(response.Status)) {
        datapoint.end();
        return response.Body;
    }

    const error = new Error(
        `${datapointName} : Response status = ${response.Status}, ServerTraceLog = ${
            response.ServerTraceLog
        }`
    );
    datapoint.endWithError(DatapointStatus.ServerError, error);
    throw error;
}
